import { Box, BoxProps, Link, LinkProps, Text } from '@chakra-ui/react';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import Markdown from '@/components/Markdown';
import { RegistryPackage } from '@/graphql/upbound-graphql';

const OverviewText: React.FC = () => (
  <Text as="span" fontWeight={900} color="black.0" fontSize="20px" lineHeight="25px" mb="20px" display="inline-block">
    Overview
  </Text>
);

const OverviewLink: React.FC<Pick<LinkProps, 'onClick' | 'children'>> = props => (
  <Link
    color="purple.0"
    display="inline-block"
    mt="15px"
    fontSize="14px"
    lineHeight="20px"
    _hover={{ textDecoration: 'underline' }}
    {...props}
  />
);

const Overview: React.FC<{
  readme: string;
  setState: Dispatch<
    SetStateAction<{
      isOpen: boolean;
    }>
  >;
}> = ({ readme, setState }) => {
  const markdownRef = useRef<HTMLDivElement>(null);
  const [showOverviewLink, setShowOverviewLink] = useState(false);
  const isTruncated = () => {
    if (!markdownRef.current) {
      return;
    }
    const truncated = markdownRef.current.clientHeight < markdownRef.current.scrollHeight;
    setShowOverviewLink(truncated);
  };

  // compare once and add resize listener on "componentDidMount"
  // remove the listener on unmount
  useEffect(() => {
    isTruncated();
    window.addEventListener('resize', isTruncated);
    return () => {
      window.removeEventListener('resize', isTruncated);
    };
  }, []);
  return (
    <>
      <Markdown
        ref={markdownRef}
        noOfLines={3}
        maxHeight="90px"
        textOverflow="ellipsis"
        overflow="hidden"
        whiteSpace="nowrap"
      >
        {readme}
      </Markdown>
      {showOverviewLink && <OverviewLink onClick={() => setState({ isOpen: true })}>Show Full Overview</OverviewLink>}
    </>
  );
};

const PackageOverview: React.FC<BoxProps & { data: RegistryPackage }> = ({ data, ...props }) => {
  const [{ isOpen }, setState] = useState({ isOpen: false });
  const readme = data.readme || '';

  if (readme) {
    if (!isOpen) {
      return (
        <Box {...props}>
          <OverviewText />
          <Overview setState={setState} readme={readme} />
        </Box>
      );
    }

    return (
      <Box {...props}>
        <OverviewText />
        <Markdown>{readme}</Markdown>
        <OverviewLink onClick={() => setState({ isOpen: false })}>Hide Full Overview</OverviewLink>
      </Box>
    );
  }

  return (
    <Box {...props}>
      <OverviewText />
      <Text lineHeight="25px" fontSize="14px" color="grey.0" fontStyle="oblique">
        No overview provided.
      </Text>
    </Box>
  );
};

export default PackageOverview;
